<template>
  <v-container>
    <h1>
      Ordre til {{ order.name }}
      <span v-if="order.order_number != null">- {{ order.order_number }}</span>
    </h1>

    <AdminBreadcrumbs :vendor="vendor" :items="breadcrumbs" />

    <v-row>
      <v-col cols="12" md="6">
        <v-card class="orange lighten-5" elevation="3">
          <v-card-title class="white--text orange">Detaljer</v-card-title>

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-list-item two-line>
                <v-list-item-content class="pt-0">
                  <v-list-item-subtitle>Dato</v-list-item-subtitle>
                  <v-list-item-title v-if="order.ordered_at">
                    {{ order.ordered_at.toLocaleDateString() }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>

            <v-col cols="12" sm="6">
              <v-list-item two-line>
                <v-list-item-content class="pt-0">
                  <v-list-item-subtitle>Ordrenummer</v-list-item-subtitle>
                  <v-list-item-title v-if="order.order_number != null">
                    {{ order.order_number }}
                  </v-list-item-title>
                  <v-list-item-title v-else> N/A </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>

            <v-col cols="12" sm="6">
              <v-list-item two-line cols="6">
                <v-list-item-content>
                  <v-list-item-subtitle>Betalingsmåte</v-list-item-subtitle>
                  <v-list-item-title>
                    <span style="text-transform: capitalize">
                      {{ order.payment }}
                    </span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>

            <v-col cols="12" sm="6">
              <v-list-item two-line cols="6">
                <v-list-item-content>
                  <v-list-item-subtitle>Navn</v-list-item-subtitle>
                  <v-list-item-title>{{ order.name }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>

            <v-col cols="12" sm="6">
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-subtitle>E-post</v-list-item-subtitle>
                  <v-list-item-title>{{ order.email }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>

            <v-col cols="12" sm="6">
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-subtitle>Mobilnummer</v-list-item-subtitle>
                  <v-list-item-title>{{ order.phone }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>

            <v-col cols="12" sm="6">
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-subtitle>Totalsum</v-list-item-subtitle>
                  <v-list-item-title>
                    kr {{ order.total }},-
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-subtitle>Levering</v-list-item-subtitle>

                  <OrderDelivered
                    v-if="deliveredDefined()"
                    :delivered="order.delivered"
                  />
                </v-list-item-content>
              </v-list-item>
            </v-col>

            <v-col cols="12" sm="6">
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-subtitle>Betalt</v-list-item-subtitle>
                  <PaymentStatus v-if="paymentStatusDefined()" :order="order" />
                </v-list-item-content>
              </v-list-item>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="orange lighten-5 mb-5" elevation="3">
          <v-card-title class="white--text orange">Levering</v-card-title>

          <v-list-item two-line>
            <v-list-item-content>
              <v-list-item-subtitle>Leveringsmetode</v-list-item-subtitle>
              <v-list-item-title>{{
                order.delivery_method_name
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item v-if="showDeliveryAddress" two-line>
            <v-list-item-content>
              <v-list-item-subtitle>Leveringsadresse</v-list-item-subtitle>
              <v-list-item-title>
                {{ order.delivery_address }}
                <br />
                {{ order.delivery_zip }}
                {{ order.delivery_region }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-container>
            <v-radio-group
              v-model="order.delivered"
              label="Levert"
              class="mt-0"
            >
              <v-radio name="delivered" label="Ja" :value="true" />
              <v-radio name="delivered" label="Nei" :value="false" />
            </v-radio-group>
          </v-container>
        </v-card>

        <PaymentDetails
          :payment="payment"
          :order="order"
          @update:payment="updatePayment()"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <OrderSummary :order="order" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import OrderDelivered from "@/components/OrderDelivered";
import OrderSummary from "@/components/OrderSummary";
import PaymentStatus from "@/components/PaymentStatus";
import PaymentDetails from "@/components/PaymentDetails";
import AdminBreadcrumbs from "@/components/AdminBreadcrumbs";
import backend from "@/backend";

async function updateOrder(order, vendorId) {
  return await backend.put(`orders/${order.id}/`, order, {
    vendor: vendorId,
  });
}

export default {
  name: "AdminOrderDetail",

  components: {
    OrderSummary,
    OrderDelivered,
    PaymentDetails,
    PaymentStatus,
    AdminBreadcrumbs,
  },

  props: {
    vendorId: {
      type: String,
      required: true,
    },
    orderId: {
      type: String,
      required: true,
    },
  },

  data: function () {
    return {
      vendor: {},
      order: {
        orderlines: [],
      },
      payment: {},
    };
  },

  computed: {
    showDeliveryAddress() {
      return this.order.delivery_address !== "";
    },
    breadcrumbs() {
      return [
        {
          text: "Ordre",
          disabled: false,
          to: { name: "adminOrders", params: { vendorId: this.vendorId } },
          exact: true,
        },
        {
          text: `Ordre til ${this.order.name}`,
          disabled: true,
        },
      ];
    },
  },

  watch: {
    "order.delivered": function (newDelivered, oldDelivered) {
      if (typeof oldDelivered === "undefined") {
        return;
      }

      this.order.delivered = newDelivered;
      updateOrder(this.order, this.vendorId);
    },
  },

  async created() {
    this.vendor = await backend.get(`vendors/${this.vendorId}/`);

    const order_data = await backend.get(`orders/${this.orderId}/`, {
      vendor: this.vendorId,
    });
    order_data["ordered_at"] = new Date(order_data["ordered_at"]);

    order_data.orderlines = await backend.get("orderlines/", {
      order: order_data.id,
    });

    for (const ol of order_data.orderlines) {
      let product_data = await backend.get(`products/${ol.product}/`, {
        vendor: this.vendor.id,
      });

      ol.product = product_data;
    }

    this.order = order_data;

    this.payment = await backend.get(`payments/${this.orderId}/`, {
      order: this.orderId,
    });
  },

  methods: {
    async updatePayment() {
      this.payment = await backend.get(`payments/${this.orderId}/`);
      this.order.payment_status = this.payment.status;
    },
    getStatusLabel(stat) {
      if (stat == "New") {
        return "Ny";
      }
      if (stat == "Completed") {
        return "Fullført";
      }
    },

    getStatusColor(stat) {
      if (stat == "New") {
        return "yellow";
      }
      if (stat == "Completed") {
        return "green";
      }
    },

    paymentStatusDefined() {
      return typeof this.order.payment_status !== "undefined";
    },

    deliveredDefined() {
      return typeof this.order.delivered !== "undefined";
    },
  },
};
</script>
